/**
 * Die Kundenkonfiguration — typisiert und prüfbar.
 *
 * Jeder Kunde hat **eine** Datei: `infra/stacks/<kunde>/stack.yaml`. Sie trägt,
 * was der Provisionierer braucht (Domains, Dienste, Dimensionierung, Ziel,
 * Sicherung) und seit dem 19.08.2026 auch, was die Anwendungen brauchen
 * (`features`, `identity`, `contacts`, `recovery`). Dieses Modul ist ihre
 * typisierte Fassung für den TypeScript-Teil des Systems.
 *
 * **Warum eine Datei und nicht zwei.** `10` §10.4 hatte ein eigenes Schema mit
 * anderen Feldnamen beschrieben — `tenantId` statt `stack_name`,
 * `domains.app` statt `domains.portal`. Gebaut wurde in `OPS-001` das andere.
 * Am 19.08.2026 entschieden: `stack.yaml` führt und wächst, §10.4 trägt den
 * Nachtrag. Zwei Dateien je Kunde wären zwei Wahrheiten über dieselben
 * Domains, und sie laufen beim ersten Umzug eines Namens auseinander.
 *
 * **Warum die Prüfung eine Liste zurückgibt und nicht wirft.** Wer beim ersten
 * Fehler abbricht, zwingt zu einem Durchlauf je Fehler — und ein
 * Provisionierungslauf, der siebenmal hintereinander mit je einem anderen
 * Befund abbricht, wird beim dritten Mal mit „das passt schon" übersprungen.
 * Der Aufrufer entscheidet, was er mit der Liste tut; abgebrochen wird dort.
 *
 * **Was hier bewusst nicht steht:** Alles, was Zustand außerhalb der Datei
 * braucht. Ob eine IP-Adresse erreichbar ist, ob ein Bucketname bereits einem
 * anderen Tenant gehört, ob eine Secret-Referenz sich auflösen lässt — das
 * sind ebenfalls Regeln aus §10.4, aber sie brauchen Netz, das Tenant-Register
 * und den age-Schlüssel. Sie gehören in den Provisionierungslauf, nicht in ein
 * Paket, das auch eine Oberfläche importieren kann.
 *
 * **Und was heute noch niemand aufruft:** `infra/stacks/provisionieren.py` ist
 * Python und prüft diese Regeln nicht — es prüft, was es selbst braucht
 * (Modulreihenfolge, Digests, vollständige Variablen). Die Regeln hier greifen
 * damit erst, wenn eine TypeScript-Anwendung die Konfiguration lädt. Das ist
 * eine Lücke, keine Doppelung: Beide prüfen verschiedene Dinge, und die
 * Provisionierung sieht diese Klasse Fehler bislang nicht.
 */

import { pruefeBranding, type Branding } from "./branding.js";
import { pruefeSecretRef } from "./secretref.js";

/**
 * Stufen aus `10` §10.4. `produktion` folgt nach der Härtung.
 *
 * **`dev` kam am 05.09.2026 dazu, weil es längst benutzt wurde.** Die
 * Entwicklungskonfiguration in `apps/portal/vite.config.ts` setzt sie seit
 * jeher — und `lesePortalKonfiguration` machte still `pilot` daraus, weil der
 * Vertrag den Wert nicht kannte. Die Oberfläche konnte deshalb nicht
 * unterscheiden, ob sie auf einem Arbeitsrechner oder bei einem Kunden läuft.
 *
 * Sie steht bewusst vorn: `10` §10.4 beschreibt Kundenstufen, und `dev` ist
 * keine — es ist die Stufe, in der es keinen Kunden gibt. Wer eine
 * Kundenstufe braucht, prüft gegen die drei dahinter.
 */
export const STUFEN = ["dev", "pilot", "staging", "produktion"] as const;
export type Stufe = (typeof STUFEN)[number];

/**
 * Die Schalter aus `10` §10.4.
 *
 * `ai` steht auf `false` und bleibt es bis Phase 8 — ADR-017 hält KI
 * vollständig hinter dem Piloten. Der Schalter existiert trotzdem, weil die
 * Alternative wäre, ihn später nachzurüsten und dabei jeden bestehenden
 * Kundenstack anzufassen.
 */
export interface Merkmale {
  readonly centralInbox: boolean;
  readonly eInvoiceInbox: boolean;
  readonly serviceReport: boolean;
  readonly technicianPwa: boolean;
  readonly ai: boolean;
  readonly recurringMaintenance: boolean;
  readonly customerPortal: boolean;
}

/**
 * Die Namen aus `stack.yaml`. Sie heißen nach dem Dienst, den sie erreichen —
 * nicht nach seiner Rolle in der Architektur, weil ein Name im Browser steht
 * und ein Benutzer ihn liest.
 *
 * `maildomain` fällt aus der Reihe: Sie ist keine Adresse eines Dienstes,
 * sondern der Teil rechts vom @. Sie darf mit einer anderen zusammenfallen.
 */
export interface Domains {
  readonly portal: string;
  readonly auth: string;
  readonly mail: string;
  readonly maildomain: string;
  readonly vikunja: string;
  readonly paperless: string;
  readonly webmail: string;
  /**
   * Nextcloud.
   *
   * **Fehlte bis zum 05.09.2026, und das war kein Versehen mit kleiner
   * Wirkung.** `10` §10.4 sah ursprünglich `app` für das Portal und `cloud`
   * für Nextcloud vor. Beim Umbau auf `stack.yaml` am 19.08.2026 wurde
   * `domains.app` zu `domains.portal` — und `cloud` fiel dabei weg. Seitdem
   * bediente Nextcloud den Namen, den die Konfiguration `portal` nennt.
   *
   * Sichtbar wurde es an einer Stelle, die niemand damit verbunden hätte: Das
   * Portal konnte keinen Verweis in die Dateiablage bilden, weil es die
   * Adresse von Nextcloud nicht kennt. Ein Name, den ein Vertrag nicht führt,
   * ist für jede Anwendung unerreichbar — auch wenn der Dienst läuft.
   */
  readonly cloud: string;
  /**
   * Die Monteur-PWA.
   *
   * Eigener Ursprung und kein Pfad unter dem Portal: `05` §5.6 verlangt für
   * den Offlinevorrat des Monteurs einen verschlüsselten Cache, und eine
   * gemeinsame Herkunft mit der Bürositzung macht diese Grenze weicher —
   * `localStorage`, Service-Worker-Bereich und Cookies gehören dann beiden.
   *
   * Der Name steht nach `07` §7.14 in der Adresszeile eines Handys. Deshalb
   * `monteur` und nicht `app`: Er wird einmal diktiert und muss ohne
   * Nachfrage ankommen.
   */
  readonly monteur: string;
}

/** Nur Adressen von Diensten — `maildomain` ist keine. */
const DIENSTNAMEN = [
  "portal", "auth", "mail", "vikunja", "paperless", "webmail",
  "cloud", "monteur",
] as const;

export interface StackKonfiguration {
  readonly stack_name: string;
  readonly domains: Domains;
  readonly acme_email?: string;
  readonly dienste?: readonly string[];
  /** Seit 19.08.2026 — die Anwendungssicht, vorher in `10` §10.4 beschrieben. */
  readonly stage?: Stufe;
  readonly features?: Merkmale;
  readonly identity?: {
    readonly provider: string;
    readonly realm: string;
    readonly bootstrapAdminSecretRef: string;
  };
  readonly contacts?: {
    readonly operator?: string;
    readonly customerAdmin?: string;
    readonly security?: string;
  };
  readonly recovery?: {
    readonly rpoStunden: number;
    readonly rtoStunden: number;
  };
  /** Markenfarbe des Betriebs. Ohne Angabe gilt der Vorgabewert der Token. */
  readonly branding?: Branding;
  /** Produktive Mailumschaltung — nur mit dokumentierter Freigabe. */
  readonly mailUmschaltung?: {
    readonly produktiv: boolean;
    readonly freigabe?: string;
  };
}

export interface Befund {
  /** `fehler` hält den Lauf an, `warnung` nicht. */
  readonly art: "fehler" | "warnung";
  readonly feld: string;
  readonly text: string;
}

/**
 * DNS-sicher heißt: als Label in einem Hostnamen verwendbar.
 *
 * Die Regel ist enger als „gültiges DNS", und zwar absichtlich: Der Wert wird
 * zum Realmnamen, zum Compose-Projektnamen, zum Präfix von Volumes und
 * Containern und zum Teil von Hostnamen. Was in einem davon zulässig ist, ist
 * es im nächsten noch lange nicht — ein Unterstrich etwa ist in Compose
 * erlaubt und in einem Hostnamen nicht.
 */
const STACK_NAME = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

/** Pilotwerte aus `01` §1.10, gegen die `10` §10.4 prüfen lässt. */
export const PILOT_RPO_STUNDEN = 24;
export const PILOT_RTO_STUNDEN = 8;

export function pruefeStackKonfiguration(
  k: StackKonfiguration,
): readonly Befund[] {
  const befunde: Befund[] = [];
  const fehler = (feld: string, text: string) =>
    befunde.push({ art: "fehler", feld, text });
  const warnung = (feld: string, text: string) =>
    befunde.push({ art: "warnung", feld, text });

  // --- stack_name ------------------------------------------------------
  if (!STACK_NAME.test(k.stack_name ?? "")) {
    fehler("stack_name",
      `"${k.stack_name}" ist nicht DNS-sicher — erlaubt sind Kleinbuchstaben, ` +
      `Ziffern und Bindestriche, nicht am Anfang oder Ende, höchstens 63 Zeichen`);
  }
  // Eindeutigkeit über alle Kunden lässt sich hier nicht entscheiden - dafür
  // braucht es das Tenant-Register. Das gehört in den Provisionierungslauf.

  // --- Hostnamen -------------------------------------------------------
  const gesehen = new Map<string, string>();
  for (const feld of DIENSTNAMEN) {
    const wert = k.domains?.[feld];
    if (typeof wert !== "string" || wert.trim() === "") {
      fehler(`domains.${feld}`, "fehlt");
      continue;
    }
    const vorher = gesehen.get(wert);
    if (vorher) {
      fehler(`domains.${feld}`,
        `"${wert}" ist bereits als domains.${vorher} vergeben — zwei Dienste ` +
        `unter demselben Namen können nicht beide ein Zertifikat bekommen`);
    } else {
      gesehen.set(wert, feld);
    }
  }
  if (!k.domains?.maildomain) {
    fehler("domains.maildomain", "fehlt");
  }

  // --- Geheimnisse -----------------------------------------------------
  if (k.identity) {
    const ref = pruefeSecretRef(
      k.identity.bootstrapAdminSecretRef, "identity.bootstrapAdminSecretRef");
    if (!ref.ref) fehler("identity.bootstrapAdminSecretRef", ref.grund);
  }

  // --- Markenfarbe -----------------------------------------------------
  // Geprueft wird sie hier und nicht in der Oberflaeche: Zu diesem Zeitpunkt
  // kostet eine Korrektur einen Anruf, spaeter einen neu auszurollenden Stack.
  for (const b of pruefeBranding(k.branding)) {
    fehler(b.feld, b.text);
  }

  // --- Wiederherstellungsziele ----------------------------------------
  if (k.recovery && (k.stage ?? "pilot") === "pilot") {
    if (k.recovery.rpoStunden > PILOT_RPO_STUNDEN) {
      fehler("recovery.rpoStunden",
        `${k.recovery.rpoStunden} h liegt über dem freigegebenen Pilotwert ` +
        `von ${PILOT_RPO_STUNDEN} h`);
    }
    if (k.recovery.rtoStunden > PILOT_RTO_STUNDEN) {
      fehler("recovery.rtoStunden",
        `${k.recovery.rtoStunden} h liegt über dem freigegebenen Pilotwert ` +
        `von ${PILOT_RTO_STUNDEN} h`);
    }
  }

  // --- Mailumschaltung -------------------------------------------------
  if (k.mailUmschaltung?.produktiv && !k.mailUmschaltung.freigabe) {
    fehler("mailUmschaltung",
      "produktive Umschaltung ohne dokumentierte Freigabe — der Schritt ist " +
      "nicht ohne Weiteres rückgängig zu machen, weil fremde Server die " +
      "MX-Antwort zwischenspeichern");
  }

  // --- Merkmale --------------------------------------------------------
  if (k.features?.ai) {
    // Kein Fehler, aber ein Zustand, der einer Entscheidung widerspricht:
    // ADR-017 hält KI vollständig hinter dem Piloten.
    warnung("features.ai",
      "eingeschaltet, obwohl ADR-017 KI hinter den Piloten stellt — der " +
      "Pilot startet nach `08` mit features.ai: false");
  }
  if ((k.stage ?? "pilot") === "pilot" && k.features?.customerPortal) {
    warnung("features.customerPortal",
      "eingeschaltet — im MVP gibt es nach `07` §7.2 keine Portalregistrierung " +
      "für externe Kunden, nur MagicLinks");
  }

  return befunde;
}

/** Kurzform für den Aufrufer: hält ein Befund den Lauf an? */
export function haeltAn(befunde: readonly Befund[]): boolean {
  return befunde.some((b) => b.art === "fehler");
}
